import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import {
  addStep,
  deleteStep,
  getJourney,
  reorderSteps,
  setJourneyStatus,
  updateJourney,
  updateStep,
  type JourneyStep,
} from "@/api/journeys";
import { listModules } from "@/api/content";
import { listSegments } from "@/api/segments";
import { Badge, Button, Card } from "@/components";
import { useAuthStore } from "@/state/auth.store";
import { useLang } from "@/i18n/LanguageContext";
import { mwdToOffset, offsetToMWD } from "./schedule";

const STATUS_TONE: Record<string, "ok" | "warn" | "neutral"> = { active: "ok", draft: "warn", archived: "neutral" };
const STATUS: Record<string, { nl: string; en: string }> = {
  draft: { nl: "concept", en: "draft" },
  active: { nl: "actief", en: "active" },
  archived: { nl: "gearchiveerd", en: "archived" },
  missing: { nl: "ontbreekt", en: "missing" },
  published: { nl: "gepubliceerd", en: "published" },
};

/** Month / week / day picker that maps to a single day offset. */
function ScheduleSelect({ offset, disabled, onChange }: { offset: number; disabled?: boolean; onChange: (offset: number) => void }) {
  const { t } = useLang();
  const { month, week, day } = offsetToMWD(offset);
  const set = (m: number, w: number, d: number) => onChange(mwdToOffset(m, w, d));
  const lab = { fontSize: 11, color: "var(--grey-text)", fontFamily: "var(--mono)" } as const;
  return (
    <span style={{ display: "flex", gap: 5, alignItems: "center" }} title={t("Maand · Week · Dag", "Month · Week · Day")}>
      <span style={lab}>{t("M", "M")}</span>
      <input type="number" min={1} max={12} disabled={disabled} value={month} style={{ width: 48 }}
        onChange={(e) => set(Math.max(1, Number(e.target.value) || 1), week, day)} />
      <span style={lab}>{t("W", "W")}</span>
      <select disabled={disabled} value={week} onChange={(e) => set(month, Number(e.target.value), day)}>
        {[1, 2, 3, 4].map((n) => <option key={n} value={n}>{n}</option>)}
      </select>
      <span style={lab}>{t("D", "D")}</span>
      <select disabled={disabled} value={day} onChange={(e) => set(month, week, Number(e.target.value))}>
        {[1, 2, 3, 4, 5, 6, 7].map((n) => <option key={n} value={n}>{n}</option>)}
      </select>
    </span>
  );
}

export function JourneyEditorPage() {
  const { id = "" } = useParams();
  const qc = useQueryClient();
  const { t } = useLang();
  const canEdit = useAuthStore((s) => s.user?.role) === "org_admin";

  const journey = useQuery({ queryKey: ["journey", id], queryFn: ({ signal }) => getJourney(id, signal) });
  const segments = useQuery({ queryKey: ["segments"], queryFn: ({ signal }) => listSegments(signal) });
  const modules = useQuery({ queryKey: ["modules"], queryFn: ({ signal }) => listModules(signal) });

  const [name, setName] = useState("");
  const [nameEn, setNameEn] = useState("");
  const [description, setDescription] = useState("");
  const [target, setTarget] = useState("");
  const [error, setError] = useState<string | null>(null);

  const [newModule, setNewModule] = useState("");
  const [newOffset, setNewOffset] = useState(0);

  useEffect(() => {
    if (journey.data) {
      setName(journey.data.name_i18n?.nl ?? journey.data.name);
      setNameEn(journey.data.name_i18n?.en ?? "");
      setDescription(journey.data.description ?? "");
      setTarget(journey.data.target_segment_id ?? "");
    }
  }, [journey.data?.id, journey.data?.updated_at]); // eslint-disable-line react-hooks/exhaustive-deps

  const invalidate = () => qc.invalidateQueries({ queryKey: ["journey", id] });
  const onErr = (e: unknown) => setError(e instanceof Error ? e.message : t("Mislukt", "Failed"));

  const saveMeta = useMutation({
    mutationFn: () => updateJourney(id, {
      name,
      name_i18n: { nl: name, ...(nameEn.trim() ? { en: nameEn.trim() } : {}) },
      description: description || null,
      target_segment_id: target || null,
    }),
    onSuccess: invalidate,
    onError: onErr,
  });
  const status = useMutation({ mutationFn: (s: "active" | "archived" | "draft") => setJourneyStatus(id, s), onSuccess: invalidate, onError: onErr });
  const add = useMutation({
    mutationFn: () => addStep(id, { module_id: newModule, required: true, available_offset_days: newOffset }),
    onSuccess: () => { setNewModule(""); setNewOffset(0); invalidate(); },
    onError: onErr,
  });
  const upd = useMutation({ mutationFn: (v: { stepId: string; required: boolean; offset: number }) => updateStep(id, v.stepId, { required: v.required, available_offset_days: v.offset }), onSuccess: invalidate, onError: onErr });
  const remove = useMutation({ mutationFn: (stepId: string) => deleteStep(id, stepId), onSuccess: invalidate, onError: onErr });
  const reorder = useMutation({ mutationFn: (ids: string[]) => reorderSteps(id, ids), onSuccess: invalidate, onError: onErr });

  if (journey.isLoading) return <div className="wrap" style={{ padding: 40 }}>{t("Laden…", "Loading…")}</div>;
  if (journey.isError || !journey.data) return <div className="wrap" style={{ padding: 40 }}>{t("Traject niet gevonden.", "Journey not found.")}</div>;
  const j = journey.data;
  const statusLabel = (s: string) => (STATUS[s] ? t(STATUS[s].nl, STATUS[s].en) : s);
  const companyWide = t("Iedereen (bedrijfsbreed)", "Everyone (company-wide)");

  function move(step: JourneyStep, dir: -1 | 1) {
    const ids = j.steps.map((s) => s.id);
    const i = ids.indexOf(step.id);
    const k = i + dir;
    if (k < 0 || k >= ids.length) return;
    [ids[i], ids[k]] = [ids[k], ids[i]];
    reorder.mutate(ids);
  }

  return (
    <section>
      <div className="wrap">
        <div className="toolbar">
          <div>
            <Link to="/app/journeys" className="eyebrow" style={{ marginBottom: 6 }}>← {t("Trajecten", "Journeys")}</Link>
            <h2 style={{ fontSize: 28 }}>{j.name}</h2>
          </div>
          <div className="spacer" />
          <Badge tone={STATUS_TONE[j.status]}>{statusLabel(j.status)}</Badge>
        </div>

        {error && <div className="form-error">{error}</div>}

        {canEdit && (
          <div className="toolbar">
            {j.status !== "active" && <Button onClick={() => status.mutate("active")} disabled={status.isPending}>{t("Activeren", "Activate")}</Button>}
            {j.status === "active" && <Button variant="outline" onClick={() => status.mutate("draft")} disabled={status.isPending}>{t("Op concept zetten", "Set to draft")}</Button>}
            <div className="spacer" />
            {j.status !== "archived" && <Button variant="outline" onClick={() => status.mutate("archived")} disabled={status.isPending}>{t("Archiveren", "Archive")}</Button>}
          </div>
        )}

        <div className="editor-grid">
          <Card>
            <p className="card-num">{t("TRAJECT", "JOURNEY")}</p>
            <div className="field">
              <label htmlFor="jn">{t("Naam (NL)", "Name (NL)")}</label>
              <input id="jn" disabled={!canEdit} value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="jne">{t("Naam (EN, optioneel)", "Name (EN, optional)")}</label>
              <input id="jne" disabled={!canEdit} value={nameEn} onChange={(e) => setNameEn(e.target.value)} placeholder={t("leeg = zelfde als NL", "empty = same as NL")} />
            </div>
            <div className="field">
              <label htmlFor="jd">{t("Omschrijving", "Description")}</label>
              <input id="jd" disabled={!canEdit} value={description} onChange={(e) => setDescription(e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="jt">{t("Doelgroep", "Audience")}</label>
              <select id="jt" disabled={!canEdit} value={target} onChange={(e) => setTarget(e.target.value)}>
                <option value="">{companyWide}</option>
                {segments.data?.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            {canEdit && <Button onClick={() => saveMeta.mutate()} disabled={saveMeta.isPending}>{t("Opslaan", "Save")}</Button>}
          </Card>

          <Card>
            <p className="card-num">{t("STAPPEN", "STEPS")}</p>
            {j.steps.length === 0 ? (
              <p className="no-data-message">{t("Nog geen stappen.", "No steps yet.")}</p>
            ) : (
              <div>
                {j.steps.map((s, idx) => (
                  <div className="step-row" key={s.id}>
                    <span className="step-handle">{idx + 1}.</span>
                    <span>
                      <strong>{s.module_title}</strong>
                      {s.module_status !== "published" && <Badge tone="warn">{statusLabel(s.module_status)}</Badge>}
                    </span>
                    <ScheduleSelect offset={s.available_offset_days} disabled={!canEdit}
                      onChange={(offset) => upd.mutate({ stepId: s.id, required: s.required, offset })} />
                    {canEdit && (
                      <span style={{ display: "flex", gap: 4 }}>
                        <button className="icon-btn" title={t("Omhoog", "Up")} onClick={() => move(s, -1)}>↑</button>
                        <button className="icon-btn" title={t("Omlaag", "Down")} onClick={() => move(s, 1)}>↓</button>
                      </span>
                    )}
                    {canEdit && <button className="icon-btn" title={t("Verwijderen", "Remove")} onClick={() => remove.mutate(s.id)}>×</button>}
                  </div>
                ))}
              </div>
            )}

            {canEdit && (
              <div style={{ marginTop: 18, borderTop: "1px solid var(--line)", paddingTop: 16 }}>
                <span style={{ fontWeight: 600, fontSize: 14 }}>{t("Stap toevoegen", "Add a step")}</span>
                <div className="field" style={{ marginTop: 8 }}>
                  <select value={newModule} onChange={(e) => setNewModule(e.target.value)}>
                    <option value="">{t("— kies een module —", "— choose a module —")}</option>
                    {modules.data?.map((m) => (
                      <option key={m.id} value={m.id}>{m.title}{m.status !== "published" ? ` (${t("concept", "draft")})` : ""}</option>
                    ))}
                  </select>
                </div>
                <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                  <ScheduleSelect offset={newOffset} onChange={setNewOffset} />
                  <Button onClick={() => add.mutate()} disabled={!newModule || add.isPending}>{t("Toevoegen", "Add")}</Button>
                </div>
                <p style={{ color: "var(--grey-text)", fontSize: 12.5, marginTop: 8 }}>
                  {t("Plan op maand · week · dag. Alleen gepubliceerde modules verschijnen in het programma.", "Schedule by month · week · day. Only published modules appear in the program.")}
                </p>
              </div>
            )}
          </Card>
        </div>
      </div>
    </section>
  );
}
